import React, { useMemo, useState } from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { includes, map, xor } from "lodash";
import { AICC_VERSION_ROW, AiccAssetType, AiccLanguageAction } from "constants/aiccLanguage";
import { AiccManagedLanguage } from "types/model/aiccLanguage";
import { AiccTtsProviderVoiceResponse } from "types/dto/response/aiccLearningAssets";
import { useAiccLanguageContentVersions } from "../../hooks/useAiccLanguageContentVersions";
import AssetSubTable from "./AssetSubTable";
import LanguageVersionRow from "./VersionRow";
import { CONTENT_VERSIONS_LOADING, WITHDRAWN_ASSETS_NOTE } from "../configs";
import { AiccLanguageVersionRow, languageVersionRows } from "../languageStatus";
import styles from "./VersionRows.module.scss";

interface Props {
  language: AiccManagedLanguage;
  onAction?: Partial<Record<AiccLanguageAction, (language: AiccManagedLanguage) => void>>;
  onManage: (language: AiccManagedLanguage, row: AiccLanguageVersionRow, assetType: AiccAssetType) => void;
  /** The language is being re-read after one of its actions, so its rows hold their buttons. */
  isRefreshing?: boolean;
  voicesById: Record<string, AiccTtsProviderVoiceResponse>;
}

/**
 * The body of a language card: one row per version of its content.
 *
 * This is where the copies are fetched, and it being a component is the whole mechanism: antd's
 * Collapse does not mount a panel's children until it is first opened, so a card that nobody touches
 * costs nothing. The list is read with `?assets=false` precisely so that this request is the only
 * place the fourteen asset breakdowns are paid for — once, for the language somebody is looking at.
 *
 * A card stays mounted once opened, so collapsing and reopening it asks for nothing further.
 */
function LanguageVersionRows({ language, onAction, onManage, isRefreshing, voicesById }: Props) {
  // Anything the list already carried is preferred: a row refreshed after an action holds copies
  // newer than a request made here could return.
  const { contentVersions: fetched, isLoading } = useAiccLanguageContentVersions(
    language.contentVersions ? undefined : language.id
  );
  const contentVersions = language.contentVersions ?? fetched;
  const rows = useMemo(() => languageVersionRows(language, contentVersions), [language, contentVersions]);
  const [openRowKeys, setOpenRowKeys] = useState<string[]>([]);

  // Branching on the flag rather than on the copies being empty: a language with no readable copy is
  // a real state, and rendering its rows from an empty list is the right answer for it.
  if (isLoading) {
    return (
      <div className={styles.versionsLoading}>
        <Spin size="small" indicator={<LoadingOutlined spin />} />
        {CONTENT_VERSIONS_LOADING}
      </div>
    );
  }

  return (
    <>
      {map(rows, (row) => (
        <LanguageVersionRow
          key={row.key}
          language={language}
          row={row}
          isOpen={includes(openRowKeys, row.key)}
          onToggle={() => setOpenRowKeys((keys) => xor(keys, [row.key]))}
          isBusy={isRefreshing}
          onAction={onAction}
        >
          <AssetSubTable
            assets={row.assets}
            note={row.kind === AICC_VERSION_ROW.withdrawn ? WITHDRAWN_ASSETS_NOTE : undefined}
            onManage={(assetType) => onManage(language, row, assetType)}
            voicesById={voicesById}
          />
        </LanguageVersionRow>
      ))}
    </>
  );
}

export default LanguageVersionRows;
